"use client";
import { Icon } from "@components/blocks/icon/Icon";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@components/ui/carousel";
import { isEmptyLogo } from "@lib/utils";
import { imageBuilder } from "@lib/utils/sanity/imgBuilder";
import { FacilityImage } from "@repo/shared-types";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Fragment, useEffect, useState } from "react";

import { ImageBlock, ImageBlockType } from "../imageBlock/ImageBlock";

interface IImgSlider {
  images: FacilityImage[];
}

export function ImgSlider({ images }: IImgSlider) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [_, setCount] = useState(0);
  const [error, setError] = useState(false);
  const t = useTranslations("Facilities");

  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length - 1);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div className="relative">
      <Carousel
        setApi={setApi}
        opts={{
          align: "start",
          loop: true,
        }}
        className="items-center overflow-hidden"
      >
        {images.length > 1 && (
          <Fragment>
            <ul className="absolute bottom-16 left-5 z-50 hidden gap-4 md:flex">
              {images.map((_, i) => (
                <li key={i}>
                  <button
                    onClick={() => api?.scrollTo(i)}
                    className={`h-4 w-4 rounded-full ${i === current ? "bg-white" : "bg-white/50"}`}
                  />
                </li>
              ))}
            </ul>
            <div className="absolute bottom-16 right-5 z-50 hidden gap-4 md:flex">
              <CarouselPrevious className="border-white bg-transparent text-white" />
              <CarouselNext className="border-white bg-transparent text-white" />
            </div>
          </Fragment>
        )}
        <CarouselContent>
          {images.map((item, index) => (
            <CarouselItem key={index} className="relative flex flex-col gap-4">
              <div className="relative aspect-video w-full basis-full">
                <Image
                  key={index}
                  src={error ? "/assets/fallback-small.png" : item.url}
                  role={item.altText ? "img" : "presentation"}
                  alt={item.altText || ""}
                  className="aspect-video w-full object-cover"
                  onError={() => setError(true)}
                  layout="fill"
                />
              </div>

              {item.photographer && (
                <figcaption className="text-sm text-grey-secondaryLight">
                  {t("Photo")}: {item.photographer}
                </figcaption>
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}

export function ImgSliderInternal({
  images,
  withBadge = false,
  logo,
}: {
  images: (ImageBlockType | null)[];
  withBadge?: boolean;
  logo?: ImageBlockType;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [_, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length - 1);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div className="relative">
      <Carousel
        setApi={setApi}
        opts={{
          align: "start",
          loop: true,
        }}
        className="items-center overflow-hidden"
      >
        {images.length > 1 && (
          <Fragment>
            <ul className="absolute bottom-5 left-5 z-50 hidden gap-4 md:flex">
              {images.map((_, i) => (
                <li key={i}>
                  <button
                    onClick={() => api?.scrollTo(i)}
                    className={`h-4 w-4 rounded-full ${i === current ? "bg-white" : "bg-white/50"}`}
                  />
                </li>
              ))}
            </ul>
            <div className="absolute bottom-5 right-5 z-50 hidden gap-4 md:flex">
              <CarouselPrevious className="border-white bg-transparent text-white" />
              <CarouselNext className="border-white bg-transparent text-white" />
            </div>
          </Fragment>
        )}
        <CarouselContent className="ml-0">
          {images.map((item, index) => (
            <CarouselItem
              key={index}
              className="relative aspect-video w-full basis-full rounded-lg"
            >
              {withBadge && (
                <div className="absolute right-5 top-5 z-[1] flex max-w-32 flex-col items-center gap-3">
                  <span>
                    <Icon iconName="loopLogo" />
                  </span>
                  {!isEmptyLogo(logo) && (
                    <div className="relative aspect-square h-16 w-16">
                      <img
                        src={logo ? imageBuilder.image(logo).url() : ""}
                        alt={logo?.alternativeText || "Certificate logo"}
                        className="h-full w-full"
                      />
                    </div>
                  )}
                </div>
              )}
              <ImageBlock
                key={index}
                sizes={["(min-width: 1440px) 720px", "100vw"]}
                imageBlock={item}
                objectFit="cover"
                className="rounded-xl"
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
